import { api, ApiError } from '@/lib/api';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import {
  serviceDetailsPayload,
  type ServiceDetailsPayload,
  type BillingServiceSnapshot,
} from './billingLineSource';

export interface ServiceDetailsBase {
  updatedAt: string;
  service: BillingServiceSnapshot;
  /** Exact submitted-to-committed identities, captured from the PUT transaction response. */
  idMap?: Record<string, string>;
}
export class ServiceDetailsConflict extends Error {
  constructor() {
    super('The same service fields changed elsewhere. Refresh and review your unsaved changes.');
  }
}
const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const collections = ['laborItems', 'parts', 'extras'] as const;

/** Three-way merge by persisted identity. Never restore a remotely removed row or discard a remote add. */
export function reconcileServiceDetails(
  base: ServiceDetailsPayload,
  desired: ServiceDetailsPayload,
  fresh: ServiceDetailsPayload
): ServiceDetailsPayload {
  const merged: ServiceDetailsPayload = {};
  for (const field of ['title', 'notes', 'fixedPriceOverrideGrossCents'] as const) {
    if (!(field in desired)) continue;
    Object.assign(merged, { [field]: fresh[field] });
    if (equal(base[field], desired[field])) continue;
    if (!equal(fresh[field], base[field]) && !equal(fresh[field], desired[field]))
      throw new ServiceDetailsConflict();
    Object.assign(merged, { [field]: desired[field] });
  }
  for (const kind of collections) {
    if (!desired[kind]) continue;
    const before = base[kind] ?? [];
    const next = desired[kind] ?? [];
    const current = fresh[kind] ?? [];
    const output = current.map((row) => ({ ...row }));
    for (const old of before) {
      if (!old.id) throw new ServiceDetailsConflict();
      const local = next.find((row) => row.id === old.id);
      const index = output.findIndex((row) => row.id === old.id);
      if (!local) {
        if (index < 0) throw new ServiceDetailsConflict();
        if (Object.keys(old).some((field) => field !== 'id' && field !== 'sortOrder' && !equal(output[index]?.[field as keyof typeof old], old[field as keyof typeof old])))
          throw new ServiceDetailsConflict();
        output.splice(index, 1);
        continue;
      }
      const changed = Object.keys(local).filter(
        (key) =>
          key !== 'id' &&
          key !== 'sortOrder' &&
          !equal(local[key as keyof typeof local], old[key as keyof typeof old])
      );
      if (!changed.length) continue;
      if (index < 0) throw new ServiceDetailsConflict();
      const target = output[index]! as unknown as Record<string, unknown>;
      for (const field of changed) {
        const was = (old as unknown as Record<string, unknown>)[field];
        const value = (local as unknown as Record<string, unknown>)[field];
        if (!equal(target[field], was) && !equal(target[field], value))
          throw new ServiceDetailsConflict();
        target[field] = value;
      }
    }
    const additions = next.filter((row) => !row.id || !before.some((old) => old.id === row.id));
    // An unknown prior PUT may already have inserted an id-less local add. Never guess its identity.
    if (additions.length && current.some((row) => !before.some((old) => old.id === row.id)))
      throw new ServiceDetailsConflict();
    for (const row of additions) output.push({ ...row });
    Object.assign(merged, { [kind]: output.map((row, sortOrder) => ({ ...row, sortOrder })) });
  }
  return merged;
}

const persistedId = (id: string | undefined) => (id && /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(id) ? { id } : {});

/**
 * Labor items and parts are updated in place by id (#1025, #3566). IDs on extras
 * are client reconciliation metadata; the legacy PUT still recreates that collection.
 */
function wirePayload(data: ServiceDetailsPayload): ServiceDetailsPayload {
  return {
    ...data,
    ...(data.laborItems ? { laborItems: data.laborItems.map(({ id, ...row }) => ({ ...row, ...persistedId(id) })) } : {}),
    ...(data.parts ? { parts: data.parts.map(({ id, ...row }) => ({ ...row, ...persistedId(id) })) } : {}),
    ...(data.extras ? { extras: data.extras.map(({ id: _id, ...row }) => row) } : {}),
  };
}
export function detailsBase(
  workOrder: WorkOrderDetail,
  bookingServiceId: string
): ServiceDetailsBase {
  const service = workOrder.services.find((s) => s.bookingServiceId === bookingServiceId);
  if (!service || !workOrder.updatedAt) throw new ServiceDetailsConflict();
  return { updatedAt: workOrder.updatedAt, service };
}

/** Both portal writers share a booking-wide queue. A 412 permits one conditional dirty-field retry. */
const queues = new Map<string, Promise<unknown>>();
export async function saveServiceDetails(input: {
  workOrderId: string;
  bookingServiceId: string;
  data: ServiceDetailsPayload;
  base: ServiceDetailsBase;
}): Promise<ServiceDetailsBase> {
  const previous = queues.get(input.workOrderId) ?? Promise.resolve();
  const task = previous
    .catch(() => {})
    .then(async () => {
      if (!input.base?.updatedAt) throw new ServiceDetailsConflict();
      const path = `/work-orders/${input.workOrderId}/services/${input.bookingServiceId}/details`;
      type Response = Pick<BillingServiceSnapshot, 'notes' | 'laborItems' | 'parts' | 'extras'> & {
        updatedAt: string;
        name?: string | null;
        title?: string | null;
        fixedPriceOverrideCents?: number | null;
        fixedPriceOverrideGrossCents?: number | null;
      };
      const put = (data: ServiceDetailsPayload, updatedAt: string) =>
        api.put<Response>(path, wirePayload(data), {
          headers: { 'If-Match': `"${updatedAt}"` },
        });
      let submitted = input.data;
      let base = input.base;
      let response: Response;
      try {
        response = await put(submitted, base.updatedAt);
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 412) throw error;
        const fresh = detailsBase(
          await api.get<WorkOrderDetail>(`/work-orders/${input.workOrderId}`),
          input.bookingServiceId
        );
        submitted = reconcileServiceDetails(
          serviceDetailsPayload(input.base.service),
          input.data,
          serviceDetailsPayload(fresh.service)
        );
        base = fresh;
        response = await put(submitted, fresh.updatedAt);
      }
      // The PUT response is the committed snapshot and token from the same transaction.
      // A later GET can fail or contain someone else's edit; it must not redefine this save.
      if (
        !response.updatedAt ||
        !Array.isArray(response.laborItems) ||
        !Array.isArray(response.parts) ||
        !Array.isArray(response.extras)
      )
        throw new ServiceDetailsConflict();
      const idMap: Record<string, string> = {};
      for (const kind of collections) {
        const rows = submitted[kind];
        if (!rows) continue;
        if (rows.length !== response[kind].length) throw new ServiceDetailsConflict();
        rows.forEach((row, i) => {
          const saved = response[kind][i];
          if (!saved?.id || saved.sortOrder !== row.sortOrder) throw new ServiceDetailsConflict();
          if (row.id) idMap[row.id] = saved.id;
        });
      }
      return { updatedAt: response.updatedAt, service: { ...base.service, ...response, name: response.name ?? base.service.name }, idMap };
    });
  queues.set(input.workOrderId, task);
  try {
    return await task;
  } finally {
    if (queues.get(input.workOrderId) === task) queues.delete(input.workOrderId);
  }
}

/** Re-map only identities proven by this PUT, then preserve edits made while it was in flight. */
export function reconcileAfterServiceDetailsSave(
  sent: ServiceDetailsPayload,
  desired: ServiceDetailsPayload,
  saved: ServiceDetailsBase
) {
  const committed = serviceDetailsPayload(saved.service);
  const baseline = { ...sent };
  const rebased = { ...desired };
  for (const kind of collections) {
    const before = sent[kind];
    if (!before || !desired[kind]) continue;
    const identity = (id: string | undefined) => id && (saved.idMap?.[id] ?? id);
    const savedRow = (id: string | undefined) => committed[kind]?.find(row => row.id === identity(id));
    // This PUT may normalize numeric fields or reconcile untouched remote changes.
    // Use its committed values as the baseline and carry only newer local edits forward.
    Object.assign(baseline, { [kind]: before.map(row => savedRow(row.id) ?? row) });
    Object.assign(rebased, { [kind]: desired[kind].map(row => {
      const old = before.find(item => item.id === row.id);
      const persisted = old && savedRow(old.id);
      if (!old || !persisted) return row;
      const result = { ...persisted } as unknown as Record<string, unknown>;
      for (const field of Object.keys(row))
        if (field !== 'id' && !equal(row[field as keyof typeof row], old[field as keyof typeof old]))
          result[field] = row[field as keyof typeof row];
      return result;
    }) });
  }
  return reconcileServiceDetails(baseline, rebased, committed);
}
